import { validateExpandedSettings } from 'containers/NestedContainers/OrderEditing/expandedSettings';
import type { OrderEditingSettings } from 'types/order-editing';
import type { BooleanFeature, EditingConfiguration, FeatureStore, GuaranteeInput, GuaranteePricing } from 'types/installationFeatureSettings';

export const featureObject = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    && [Object.prototype, null].includes(Object.getPrototypeOf(value)) ? value as Record<string, unknown> : null;
const own = (value: Record<string, unknown>, key: string) => Object.prototype.hasOwnProperty.call(value, key);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const nullableNumber = (value: unknown) => value === null || finite(value);
const canonical = (value: unknown) => typeof value === 'string' ? value.trim().toLowerCase() : '';

export function readFeatureStore(value: unknown, storeId: string, domain: string): FeatureStore | null {
  const record = featureObject(value);
  if (!record || !own(record, 'id') || record.id !== storeId || !own(record, 'store')
    || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(canonical(record.store))
    || canonical(record.store) !== canonical(domain) || !own(record, 'currency')
    || typeof record.currency !== 'string' || !/^[A-Z]{3}$/.test(record.currency)
    || !own(record, 'uninstalledAt') || record.uninstalledAt !== null) return null;
  return record as unknown as FeatureStore;
}

export function featureChoice(feature: BooleanFeature, store: FeatureStore | null): boolean | null {
  if (!store) return null;
  if (feature !== 'tracking') {
    const record = feature === 'gifts' ? featureObject(store.freeGifts)
      : featureObject(featureObject(store.policySettings)?.returns);
    return record && own(record, 'enabled') && typeof record.enabled === 'boolean' ? record.enabled : null;
  }
  const features = featureObject(store.features);
  const billing = featureObject(features?.trackingBilling);
  if (!features || !billing || !own(features, 'enableTracking') || !own(billing, 'allOrdersEnabled')) return null;
  if (features.enableTracking === false && billing.allOrdersEnabled === false) return false;
  if (features.enableTracking !== true || billing.allOrdersEnabled !== true
    || billing.feePerOrderUsd !== 0.05 || billing.termsVersion !== 1
    || typeof billing.acceptedAt !== 'string' || !Number.isFinite(Date.parse(billing.acceptedAt))) return null;
  return true;
}

export function readGuaranteePricing(value: unknown, storeId: string): GuaranteePricing | null {
  if (!Array.isArray(value) || value.length !== 1) return null;
  const record = featureObject(value[0]);
  const amounts = ['defaultFee', 'percentage', 'minimumFee', 'maximumFee', 'highestOrderValue'];
  if (!record || record.storeId !== storeId || typeof record.id !== 'string' || !record.id
    || typeof record.updatedAt !== 'string' || !Number.isFinite(Date.parse(record.updatedAt))
    || !['FIXED', 'PERCENTAGE'].includes(String(record.protectionType))
    || amounts.some(key => !own(record, key) || !nullableNumber(record[key])) || !own(record, 'rules')) return null;
  if (record.protectionType === 'FIXED' && !finite(record.defaultFee)) return null;
  if (record.protectionType === 'PERCENTAGE' && (!finite(record.percentage) || record.percentage > 1
    || !finite(record.minimumFee) || !finite(record.maximumFee) || record.minimumFee > record.maximumFee)) return null;
  if (record.rules !== null) {
    if (!Array.isArray(record.rules) || record.rules.length < 1 || record.rules.length > 100) return null;
    for (const item of record.rules) {
      const rule = featureObject(item);
      if (!rule || Object.keys(rule).some(key => !['name', 'fee', 'rangeLower', 'rangeUpper'].includes(key))
        || !finite(rule.fee) || !finite(rule.rangeLower)
        || (own(rule, 'rangeUpper') && !nullableNumber(rule.rangeUpper))
        || (own(rule, 'name') && rule.name !== null && typeof rule.name !== 'string')) return null;
    }
  }
  return record as unknown as GuaranteePricing;
}

export function prepareGuaranteeInput(pricing: GuaranteePricing | null, text: string): GuaranteeInput | null {
  if (!pricing || !/^\d+(?:\.\d+)?$/.test(text)) return null;
  const value = Number(text);
  if (!finite(value) || (pricing.protectionType === 'PERCENTAGE' && value > 100)) return null;
  return { storeId: pricing.storeId, type: pricing.protectionType,
    defaultFee: pricing.protectionType === 'FIXED' ? value : pricing.defaultFee,
    percentage: pricing.protectionType === 'PERCENTAGE' ? value / 100 : pricing.percentage,
    minimumFee: pricing.minimumFee, maximumFee: pricing.maximumFee,
    highestOrderValue: pricing.highestOrderValue, rules: pricing.rules };
}

export function readEditingConfiguration(value: unknown, storeId: string): EditingConfiguration | null {
  const record = featureObject(value), settings = featureObject(record?.settings), setup = featureObject(record?.setup);
  const keys = ['deadlineMinutes', 'editShipping', 'cancelOrder', 'allowPoBoxes', 'verifyAddresses', 'allowUnverified',
    'protectedOnly', 'sameCountryOnly', 'guestAccess', 'notifications', 'appearance', 'expanded', 'catalog', 'support', 'cancellation', 'shippingOffers'];
  const booleans = ['editShipping', 'cancelOrder', 'allowPoBoxes', 'verifyAddresses', 'allowUnverified', 'protectedOnly', 'sameCountryOnly', 'guestAccess'];
  const appearance = featureObject(settings?.appearance), notifications = featureObject(settings?.notifications);
  const appearanceKeys = ['locale', 'title', 'subtitle', 'editTitle', 'cancelTitle', 'cancelDescription', 'saveLabel', 'cancelLabel', 'confirmLabel', 'backLabel', 'buttonVariant', 'buttonTone'];
  if (!record || record.storeId !== storeId || typeof record.revision !== 'number'
    || !Number.isInteger(record.revision) || record.revision < 0 || !settings || !setup
    || Object.keys(settings).some(key => !keys.includes(key))
    || !Number.isInteger(settings.deadlineMinutes) || Number(settings.deadlineMinutes) < 1 || Number(settings.deadlineMinutes) > 14400
    || booleans.some(key => !own(settings, key) || typeof settings[key] !== 'boolean')
    || !appearance || Object.keys(appearance).some(key => !appearanceKeys.includes(key))
    || appearanceKeys.some(key => {
      const text = appearance[key];
      // Match the existing server's plain-text and length limits without normalizing saved values.
      // eslint-disable-next-line no-control-regex
      return typeof text !== 'string' || text.trim() !== text || /[<>\x00-\x08\x0B\x0C\x0E-\x1F]/.test(text)
        || text.length > (['subtitle', 'cancelDescription'].includes(key) ? 500 : 120);
    })
    || !['auto', 'en', 'es', 'fr'].includes(String(appearance.locale))
    || !['primary', 'secondary', 'plain'].includes(String(appearance.buttonVariant))
    || !['auto', 'critical'].includes(String(appearance.buttonTone))
    || !notifications || Object.keys(notifications).some(key => !['address', 'cancellation'].includes(key))
    || ['address', 'cancellation'].some(key => typeof notifications[key] !== 'boolean')
    || !Array.isArray(setup.checks)) return null;
  try { if (validateExpandedSettings(settings as unknown as OrderEditingSettings)) return null; } catch { return null; }
  return record as unknown as EditingConfiguration;
}

export function editingBillingPrice(value: unknown): number | null {
  const billing = featureObject(value), cents = billing?.priceCents;
  // Consume the scoped backend quote; the billing provider currently supports these USD rates.
  return billing && own(billing, 'priceCents') && typeof cents === 'number' && Number.isSafeInteger(cents)
    && [5, 10].includes(cents) && own(billing, 'currency') && billing.currency === 'USD' ? cents : null;
}

export function editingAvailable(config: EditingConfiguration | null, action: 'address' | 'cancellation'): boolean {
  if (!config) return false;
  const required = ['app_identity', 'order_scopes', 'checkout_profile'];
  if (action === 'address' && config.settings.verifyAddresses) required.push('address_verification');
  const billing = featureObject(config.setup.billing);
  return editingBillingPrice(billing) !== null && !!billing && own(billing, 'status') && billing.status === 'ready' && required.every(id => {
    const matches = config.setup.checks.filter(check => check?.id === id);
    return matches.length === 1 && matches[0].status === 'ready';
  });
}
